import { useState, useMemo, useEffect } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/class-control";
import { Search, Filter, Eye, Pencil, Trash2 } from "lucide-react";
import Pagination from "@/components/dashboard/pagination";
import ContractFilterDialog, { IContractFilters } from "@/pages/contrato/components/contract-filter-dialog";
import { useGetContractsByMovie } from "@/services/queries/useContratos";
import type { IContract, ContractStatus } from "@/interfaces/contracts.interface";

export interface IProjectContract {
  id: number;
  personName: string;
  personAvatar?: string;
  phone: string;
  email: string;
  team: string;
  status: "Em Execução" | "Vencendo" | "Solicitado" | "Concluído";
  date: string;
}

interface ProjectContractsContentProps {
  movieId: number;
  name: string;
  salicNumber: string;
  proponent: string;
  cnpj: string;
}

// Map backend contract status to UI status
const mapContractStatus = (status: ContractStatus): IProjectContract["status"] => {
  switch (status) {
    case "ACTIVE":
      return "Em Execução";
    case "SIGNED":
      return "Concluído";
    case "PENDING_CONTRACTOR_ACCEPTANCE":
    case "PENDING_EXECUTIVE_APPROVAL":
    case "PENDING_ATL_REVIEW":
      return "Solicitado";
    case "DRAFT":
    case "REJECTED":
    default:
      return "Solicitado";
  }
};

// Transform backend contract to UI format
const transformContract = (contract: IContract): IProjectContract => {
  return {
    id: contract.id,
    personName: contract.staffUser?.person?.name || "N/A",
    phone: contract.staffUser?.person?.cellphone_number || "N/A",
    email: contract.staffUser?.login || "N/A",
    team: contract.staffUser?.person?.role || "N/A",
    status: mapContractStatus(contract.contractStatus),
    date: new Date(contract.createdAt).toLocaleDateString("pt-BR"),
  };
};

export function ProjectContractsContent({
  movieId,
  name,
  salicNumber,
  proponent,
  cnpj,
}: ProjectContractsContentProps) {
  // Fetch contracts from API
  const { data: contractsResponse, isLoading, error } = useGetContractsByMovie(movieId);

  // Transform backend data to UI format
  const contracts = useMemo(() => {
    if (!contractsResponse?.data) return [];
    return contractsResponse?.data.map(transformContract);
  }, [contractsResponse]);
  const [, setLocation] = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState<IContractFilters>({
    status: [],
    dateFrom: "",
    dateTo: "",
    team: [],
  });

  const itemsPerPage = 8;

  // Filter contracts based on search term and filters
  const filteredContracts = useMemo(() => {
    let result = contracts;

    // Search filter
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      result = result.filter(
        (contract) =>
          contract.personName.toLowerCase().includes(lowerSearch) ||
          contract.email.toLowerCase().includes(lowerSearch) ||
          contract.phone.toLowerCase().includes(lowerSearch) ||
          contract.team.toLowerCase().includes(lowerSearch)
      );
    }

    // Status filter
    if (filters.status.length > 0) {
      result = result.filter((contract) => filters.status.includes(contract.status));
    }

    // Team filter
    if (filters.team.length > 0) {
      result = result.filter((contract) => filters.team.includes(contract.team));
    }

    // Date filter
    if (filters.dateFrom || filters.dateTo) {
      result = result.filter((contract) => {
        const contractDate = new Date(contract.date.split("/").reverse().join("-"));
        const fromDate = filters.dateFrom ? new Date(filters.dateFrom) : null;
        const toDate = filters.dateTo ? new Date(filters.dateTo) : null;

        if (fromDate && contractDate < fromDate) return false;
        if (toDate && contractDate > toDate) return false;
        return true;
      });
    }

    return result;
  }, [searchTerm, filters, contracts]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredContracts.length / itemsPerPage);
  const paginatedContracts = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredContracts.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredContracts, currentPage]);

  // Reset to page 1 when search or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters]);

  // Handle filter apply
  const handleApplyFilters = (newFilters: IContractFilters) => {
    setFilters(newFilters);
    setFilterDialogOpen(false);
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const StatusBadge = ({ status }: { status: IProjectContract["status"] }) => {
    const getStatusColor = () => {
      switch (status) {
        case "Em Execução":
          return "bg-[#F7DC4D] text-[#1D1D1D]";
        case "Vencendo":
          return "bg-[#DC4426] text-[#F6F6F6]";
        case "Solicitado":
          return "bg-[#7846FF] text-[#F5F2FF]";
        case "Concluído":
          return "bg-[#09B068] text-[#F6F6F6]";
        default:
          return "bg-gray-500/20 text-gray-400";
      }
    };

    return (
      <div
        className={cn(
          "rounded-[20px] px-[10px] h-[39px] inline-flex items-center justify-center min-w-[106px]",
          getStatusColor()
        )}
      >
        <p className="font-['Plus_Jakarta_Sans'] font-normal text-[16px] text-center">
          {status}
        </p>
      </div>
    );
  };

  const handleView = (contractId: number) => {
    setLocation(`/contratual/contratos/${contractId}`);
  };

  const handleEdit = (contractId: number) => {
    setLocation(`/contratual/contratos/${contractId}/editar`);
  };

  const handleDelete = (contractId: number) => {
    // TODO: Implement delete confirmation dialog
    console.log("Delete contract:", contractId);
  };

  const handleGoToContracts = () => {
    setLocation("/contratual/contratos");
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="bg-[#15203C] rounded-[20px] px-[30px] py-[30px] flex items-center justify-center min-h-[400px]">
        <p className="text-gray-400">Carregando contratos...</p>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="bg-[#15203C] rounded-[20px] px-[30px] py-[30px] flex items-center justify-center min-h-[400px]">
        <p className="text-red-400">Erro ao carregar contratos. Tente novamente.</p>
      </div>
    );
  }

  return (
    <div className="bg-[#15203C] rounded-[20px] px-[30px] py-[30px] flex flex-col gap-[40px]">
      {/* Header Section */}
      <div className="flex gap-[612px] items-start">
        <div className="flex flex-col gap-[8px] items-start w-[301px]">
          <p className="font-['Plus_Jakarta_Sans'] font-bold text-[24px] text-white">
            Contratos do Projeto - {name}
          </p>
          <div className="flex gap-[10px] items-start font-['Plus_Jakarta_Sans'] font-normal text-[14px] text-[#D1D1D1] whitespace-pre">
            <p>
              <span>Número Salic: </span>
              <span className="font-semibold text-[#F6F6F6]">{salicNumber}</span>
            </p>
            <p>
              <span>Produtora: </span>
              <span className="font-semibold text-[#F6F6F6]">
                {proponent} | {cnpj}
              </span>
            </p>
          </div>
        </div>

        {/* Search and Filter Controls */}
        <div className="flex gap-[10px] items-center">
          {/* Search */}
          <div className="border border-[#5D5D5D] border-solid box-border flex gap-[90px] h-[50px] items-center px-[20px] py-[10px] rounded-[10px] w-[227px]">
            <input
              type="text"
              placeholder="Procurar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 bg-transparent outline-none font-['Plus_Jakarta_Sans'] font-normal text-[16px] text-[#F6F6F6] placeholder-[#F6F6F6]"
            />
            <Search className="w-[24px] h-[24px] text-white" />
          </div>

          {/* Filter Button */}
          <div
            onClick={() => setFilterDialogOpen(true)}
            className="border border-[#5D5D5D] border-solid box-border flex gap-[20px] h-[50px] items-center px-[20px] py-[10px] rounded-[10px] w-[117px] cursor-pointer hover:bg-[#1a2642] transition-colors"
          >
            <p className="font-['Plus_Jakarta_Sans'] font-normal text-[16px] text-[#F6F6F6] whitespace-pre">
              Filtro
            </p>
            <Filter className="w-[24px] h-[24px] text-white" />
          </div>

          {/* Ir para Contratos Button */}
          <Button
            onClick={handleGoToContracts}
            className="bg-[#7846FF] hover:bg-[#7846FF]/90 flex gap-[3px] h-[50px] items-center justify-center px-[10px] rounded-[10px] w-[161px]"
          >
            <span className="font-['Plus_Jakarta_Sans'] font-bold text-[16px] text-[#F5F2FF]">
              Ir para Contratos
            </span>
          </Button>
        </div>
      </div>

      {/* Table Section */}
      <div className="flex flex-col gap-[24px] items-start w-full">
        <div className="flex flex-col gap-px items-start w-full">
          {/* Table Header */}
          <div className="bg-[#0C1427] box-border flex gap-[59px] h-[42px] items-center px-[20px] py-[10px] rounded-[8px] w-full">
            <p className="font-['Plus_Jakarta_Sans'] font-bold text-[14px] text-[#F6F6F6] w-[160px]">
              Nome
            </p>
            <p className="font-['Plus_Jakarta_Sans'] font-bold text-[14px] text-[#F6F6F6] w-[160px]">
              Telefone
            </p>
            <p className="font-['Plus_Jakarta_Sans'] font-bold text-[14px] text-[#F6F6F6] w-[220px]">
              E-mail
            </p>
            <p className="font-['Plus_Jakarta_Sans'] font-bold text-[14px] text-[#F6F6F6] w-[151px]">
              Equipe/Função
            </p>
            <p className="font-['Plus_Jakarta_Sans'] font-bold text-[14px] text-[#F6F6F6] w-[120px]">
              Status
            </p>
            <p className="font-['Plus_Jakarta_Sans'] font-bold text-[14px] text-[#F6F6F6] w-[140px]">
              Data
            </p>
            <p className="font-['Plus_Jakarta_Sans'] font-bold text-[14px] text-[#F6F6F6] w-[123px]">
              Ações
            </p>
          </div>

          {/* Table Rows */}
          {paginatedContracts.map((contract, index) => (
            <div key={contract.id}>
              <div className="box-border flex gap-[59px] items-center px-[20px] py-[10px]">
                {/* Nome */}
                <div className="box-border flex gap-[10px] items-center px-0 py-[10px] w-[160px]">
                  <div className="w-[39px] h-[39px] rounded-full bg-gray-600 flex items-center justify-center flex-shrink-0">
                    {contract.personAvatar ? (
                      <img
                        src={contract.personAvatar}
                        alt={contract.personName}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <span className="font-['Plus_Jakarta_Sans'] text-xs font-medium text-white">
                        {getInitials(contract.personName)}
                      </span>
                    )}
                  </div>
                  <p className="font-['Plus_Jakarta_Sans'] font-semibold text-[16px] text-[#F6F6F6] whitespace-pre">
                    {contract.personName}
                  </p>
                </div>

                {/* Telefone */}
                <p className="font-['Plus_Jakarta_Sans'] font-normal text-[16px] text-[#F6F6F6] w-[160px]">
                  {contract.phone}
                </p>

                {/* E-mail */}
                <p className="font-['Plus_Jakarta_Sans'] font-normal text-[16px] text-[#F6F6F6] w-[220px]">
                  {contract.email}
                </p>

                {/* Equipe/Função */}
                <div className="box-border flex gap-[10px] items-center px-0 py-[10px] w-[151px]">
                  <p className="font-['Plus_Jakarta_Sans'] font-normal text-[16px] text-[#F6F6F6] whitespace-pre">
                    {contract.team}
                  </p>
                </div>

                {/* Status */}
                <div className="flex gap-[10px] items-center justify-center w-[120px]">
                  <StatusBadge status={contract.status} />
                </div>

                {/* Data */}
                <div className="box-border flex gap-[10px] items-center px-0 py-[10px] w-[140px]">
                  <p className="font-['Plus_Jakarta_Sans'] font-normal text-[16px] text-[#F6F6F6] whitespace-pre">
                    {contract.date}
                  </p>
                </div>

                {/* Ações */}
                <div className="box-border flex gap-[10px] items-center p-[10px] rounded-[30px] w-[123px]">
                  <button
                    onClick={() => handleView(contract.id)}
                    className="text-white hover:text-[#13D480] transition-colors"
                    aria-label="Visualizar contrato"
                  >
                    <Eye className="w-[24px] h-[24px]" />
                  </button>
                  <button
                    onClick={() => handleEdit(contract.id)}
                    className="text-white hover:text-[#7846FF] transition-colors"
                    aria-label="Editar contrato"
                  >
                    <Pencil className="w-[24px] h-[24px]" />
                  </button>
                  <button
                    onClick={() => handleDelete(contract.id)}
                    className="text-white hover:text-[#DC4426] transition-colors"
                    aria-label="Excluir contrato"
                  >
                    <Trash2 className="w-[24px] h-[24px]" />
                  </button>
                </div>
              </div>

              {/* Divider Line */}
              {index < paginatedContracts.length - 1 && (
                <div className="h-0 border-t border-[#2A3A5C] w-full" />
              )}
            </div>
          ))}
        </div>

        {/* Pagination Section */}
        <div className="flex gap-[962px] items-center w-full">
          <p className="font-['Plus_Jakarta_Sans'] font-normal text-[14px] text-[#888888] whitespace-pre">
            Exibindo {paginatedContracts.length} de {filteredContracts.length} transações
          </p>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>

      {/* Filter Dialog */}
      <ContractFilterDialog
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
        filters={filters}
        onApply={handleApplyFilters}
      />
    </div>
  );
}
